import { useEffect, useRef, useState } from 'react';

import { IS_PRODUCTION, VERSION } from '../../../../config';

import { compareAppVersions } from '../../utils/compareAppVersions';

const VERSION_ENDPOINT = '/api/version';
const POLLING_INTERVAL_MS = 15 * 60 * 1000;
const MIN_REQUEST_GAP_MS = 30 * 1000;
const MOUNT_DELAY_MS = 10 * 1000;

interface UseAppVersionCheckResult {
  isOutdated: boolean;
}

function useAppVersionCheck(): UseAppVersionCheckResult {
  const [isOutdated, setIsOutdated] = useState(false);

  const lastFetchAtRef = useRef(0);

  const abortRef = useRef<AbortController | null>(null);

  const isOutdatedRef = useRef(false);

  useEffect(() => {
    if (!IS_PRODUCTION) {
      return undefined;
    }

    const checkVersion = async () => {
      if (isOutdatedRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastFetchAtRef.current < MIN_REQUEST_GAP_MS) {
        return;
      }
      lastFetchAtRef.current = now;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${VERSION_ENDPOINT}?_=${now}`, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as { version?: unknown };

        if (typeof data?.version !== 'string') {
          return;
        }

        if (compareAppVersions(data.version, VERSION) === 1) {
          isOutdatedRef.current = true;

          setIsOutdated(true);
        }
      } catch {
        // Network error or aborted request — silently retry on next trigger
      }
    };

    const mountTimer = window.setTimeout(checkVersion, MOUNT_DELAY_MS);
    const intervalId = window.setInterval(checkVersion, POLLING_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    const handleFocus = () => checkVersion();
    const handleOnline = () => checkVersion();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      window.clearTimeout(mountTimer);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      abortRef.current?.abort();
    };
  }, []);

  return { isOutdated };
}

export default useAppVersionCheck;
